/**
 * FairnessFlagPanel
 *
 * Facilitator / cycle-owner view of process-fairness flags raised by participants
 * for a session. Closes the loop: a flag can be acknowledged, resolved with a
 * note, or dismissed. Open flags are surfaced prominently so they aren't ignored.
 */

import { useEffect, useState } from 'react';
import { Flag, ShieldCheck, Check, MessageSquareWarning } from 'lucide-react';
import { useFairnessFlagStore } from '../../../stores/prioritization/fairnessFlagStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { isUuid } from '../../../lib/isUuid';
import {
  FAIRNESS_CONCERN_LABELS,
  type FairnessFlag,
  type FairnessFlagStatus,
} from '../../../types/prioritization/fairnessFlag';

const STATUS_STYLE: Record<FairnessFlagStatus, string> = {
  open: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  acknowledged: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
  resolved: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  dismissed: 'bg-[var(--hover-tint)] text-[var(--text-ter)]',
};

function FlagRow({ flag }: { flag: FairnessFlag }) {
  const resolveFlag = useFairnessFlagStore(s => s.resolveFlag);
  const userId = useUserStore(s => s.userId);
  const userName = useUserStore(s => s.userName);
  const [resolving, setResolving] = useState(false);
  const [note, setNote] = useState('');
  const [showResolve, setShowResolve] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // `acknowledged_by` is a uuid column; never invent an actor for it (#621).
  const canAct = isUuid(userId);

  const act = async (status: 'acknowledged' | 'resolved' | 'dismissed') => {
    if (!canAct) {
      setError('No signed-in user to record this action against. Sign in again and retry.');
      return;
    }
    setResolving(true);
    setError(null);
    const result = await resolveFlag({
      flagId: flag.id,
      status,
      actorId: userId,
      actorName: userName || 'Facilitator',
      resolutionNote: note || undefined,
    });
    setResolving(false);
    if (result.error) {
      // The row did not move — keep the form open and say so; the flag's
      // rendered status comes from the store and stays where the DB left it.
      setError(result.error);
      return;
    }
    setShowResolve(false);
  };

  const isClosed = flag.status === 'resolved' || flag.status === 'dismissed';

  return (
    <li className="px-5 py-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-[var(--text-pri)]">
              {FAIRNESS_CONCERN_LABELS[flag.concernType]}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-[0.65rem] font-semibold uppercase tracking-wide ${STATUS_STYLE[flag.status]}`}>
              {flag.status}
            </span>
          </div>
          <p className="text-xs text-[var(--text-ter)] mt-0.5">
            Raised by {flag.raisedByName || 'a participant'} ·{' '}
            {new Date(flag.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </p>
          {flag.note && (
            <p className="mt-1.5 text-sm text-[var(--text-sec)] italic border-l-2 border-[var(--mustard)] pl-2">
              "{flag.note}"
            </p>
          )}
          {flag.resolutionNote && (
            <p className="mt-1.5 text-xs text-[var(--green)] dark:text-[var(--text-ter)]">
              Response: {flag.resolutionNote}
              {flag.acknowledgedByName && <span className="text-[var(--text-ter)]"> — {flag.acknowledgedByName}</span>}
            </p>
          )}
        </div>

        {!isClosed && (
          <div className="flex-shrink-0 flex items-center gap-1.5">
            {flag.status === 'open' && (
              <button
                onClick={() => void act('acknowledged')}
                disabled={resolving || !canAct}
                className="text-xs font-medium px-2.5 py-1.5 border border-[var(--slate)] text-[var(--text-ter)] hover:bg-[var(--slate-dim)] disabled:opacity-50"
              >
                Acknowledge
              </button>
            )}
            <button
              onClick={() => setShowResolve(v => !v)}
              disabled={resolving || !canAct}
              className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 border border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)] disabled:opacity-50"
            >
              <Check className="h-3 w-3" />
              Resolve
            </button>
          </div>
        )}
      </div>

      {!isClosed && !canAct && (
        <p className="mt-2 text-xs text-[var(--coral)]">Sign in to acknowledge or resolve this concern.</p>
      )}
      {error && !isClosed && (
        <p role="alert" className="mt-2 text-xs text-[var(--coral)]">{error}</p>
      )}

      {showResolve && !isClosed && (
        <div className="mt-2.5 ml-0 space-y-2">
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={2}
            placeholder="How was this addressed? (closes the loop for the participant)"
            className="w-full text-sm border border-[var(--rule)] px-3 py-2 focus:ring-2 focus:ring-emerald-400 focus:border-[var(--green)] resize-none"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={() => void act('resolved')}
              disabled={resolving || !canAct}
              className="text-xs font-semibold px-3 py-1.5 bg-[var(--green)] text-white hover:bg-[var(--green)] disabled:opacity-50"
            >
              Mark resolved
            </button>
            <button
              onClick={() => void act('dismissed')}
              disabled={resolving || !canAct}
              className="text-xs font-medium px-3 py-1.5 border border-[var(--rule)] text-[var(--text-ter)] hover:bg-[var(--surface)] disabled:opacity-50"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

interface FairnessFlagPanelProps {
  sessionId: string;
}

export function FairnessFlagPanel({ sessionId }: FairnessFlagPanelProps) {
  const fetchForSession = useFairnessFlagStore(s => s.fetchForSession);
  const flags = useFairnessFlagStore(s => s.flags);

  useEffect(() => {
    void fetchForSession(sessionId);
  }, [sessionId, fetchForSession]);

  const sessionFlags = flags
    .filter(f => f.sessionId === sessionId)
    .sort((a, b) => {
      // Open first, then by recency.
      if (a.status === 'open' && b.status !== 'open') return -1;
      if (b.status === 'open' && a.status !== 'open') return 1;
      return b.createdAt.localeCompare(a.createdAt);
    });

  if (sessionFlags.length === 0) return null;

  const openCount = sessionFlags.filter(f => f.status === 'open').length;

  return (
    <div className={` border overflow-hidden ${openCount > 0 ? 'border-[var(--mustard)]' : 'border-[var(--rule)]'} bg-[var(--bg-raised)]`}>
      <div className={`flex items-center gap-3 px-5 py-4 border-b ${openCount > 0 ? 'bg-[var(--mustard-dim)] border-[var(--mustard)]' : 'bg-[var(--surface)] border-[var(--rule)]'}`}>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${openCount > 0 ? 'bg-[var(--mustard-dim)]' : 'bg-[var(--hover-tint)]'}`}>
          {openCount > 0 ? (
            <MessageSquareWarning className="h-4 w-4 text-[var(--mustard)]" />
          ) : (
            <ShieldCheck className="h-4 w-4 text-[var(--text-ter)]" />
          )}
        </div>
        <div>
          <p className="text-sm font-semibold text-[var(--text-pri)] flex items-center gap-1.5">
            <Flag className="h-3.5 w-3.5 text-[var(--mustard)]" />
            Process concerns — {sessionFlags.length}
          </p>
          <p className="text-xs text-[var(--text-ter)]">
            {openCount > 0
              ? `${openCount} open — acknowledge or resolve so the loop closes.`
              : 'All raised concerns have been addressed.'}
          </p>
        </div>
      </div>
      <ul className="divide-y divide-[var(--rule)]">
        {sessionFlags.map(flag => (
          <FlagRow key={flag.id} flag={flag} />
        ))}
      </ul>
    </div>
  );
}

export default FairnessFlagPanel;
